/**
 * @render react
 * @name WhichIsBest
 * @example
 * <WhichIsBest description="component description" columns={[{ title: 'Content 1', tooltipTitle: 'Tooltip 1', excerpt: 'Excerpt 1', features: [{ title: 'feature 1', value: 'value 1' }, { title: 'feature 2', value: 'value 2' }]}, {title: 'Content 1', tooltipTitle: 'Tooltip 1', excerpt: 'Excerpt 1',features: [{ title: 'feature 1', value: 'value 1' }, { title: 'feature 2', value: 'value 2' }]} ]} />
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Heading } from 'Components/molecules/Heading';
import { Link } from 'Components/molecules/Link';
import { Tooltip } from 'Components/molecules/Tooltip';

import './_style.scss';

/**
 * WhichIsBest component
 * @param {string} title - Title of the component
 * @param {array} columns - Array of columns to display
 * @param {string} description - Description of the component to display
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {Object} context - Extra information coming from Polaris provider.
 */
const WhichIsBest = (
  {
    title,
    columns,
    description,
    loadStyles,
    extraClassNames,
    context: { cssPrefix },
  },
) => {
  const handleMarketplaceLinkClick = (tracking) => {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      if (tracking) {
        window.dataLayer.push(tracking);
      }
    }
  };

  return (
    <div
      className={classNames({
        [`${cssPrefix}__whichisbest`]: loadStyles,
        ...extraClassNames,
      })}
    >
      {title && <Heading size={4} extraClassNames={{ [`${cssPrefix}__whichisbest--title`]: true }}>{title}</Heading>}

      <div className={`${cssPrefix}__whichisbest--column-container`}>
        {columns.map((column, index) => (
          <div key={index} className={`${cssPrefix}__whichisbest--column`}>
            {column.title && (
              <p className={`${cssPrefix}__whichisbest--column-title`}>
                {column.title}

                {column.tooltipTitle
                  && (
                  <Tooltip
                    icon={column.tooltipIcon}
                    closeLabel={column.tooltipCloseLabel}
                    title={column.tooltipTitle}
                    content={column.tooltipContent}
                    extraClassNames={{ [`${cssPrefix}__whichisbest--tooltip`]: true }}
                  />
                  )}
              </p>
            )}

            {column.excerpt && <div className={`${cssPrefix}__whichisbest--column-excerpt`}>{column.excerpt}</div>}

            {column.features && (
              <ul className={`${cssPrefix}__whichisbest--features`}>
                {column.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className={`${cssPrefix}__whichisbest--feature`}>
                    {feature.title && (
                      <div className={`${cssPrefix}__whichisbest--feature-title`}>{feature.title}</div>
                    )}
                    {feature.value && (
                      <div className={`${cssPrefix}__whichisbest--feature-value`}>{feature.value}</div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {column.marketplaceLink && (
              <Link
                href={column.marketplaceLink.url}
                extraClassNames={{ [`${cssPrefix}__whichisbest--link`]: true }}
                onClick={() => handleMarketplaceLinkClick(column.marketplaceLink.tracking)}
              >
                {column.marketplaceLink.title}
              </Link>
            )}
          </div>
        ))}
      </div>
      {description && (
        <div className={`${cssPrefix}__whichisbest--description`}>
          {description}
        </div>
      )}
    </div>
  );
};

/**
 * Valid Props
 */
WhichIsBest.propTypes = {
  title: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape()),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
  context: PropTypes.shape(),
};

/**
 * Default Props
 */
WhichIsBest.defaultProps = {
  title: 'Which is Best?',
  columns: [],
  description: '',
  loadStyles: true,
  extraClassNames: {},
  context: {},
};

export default withPolaris(WhichIsBest);
